import { PanelLeftClose, PanelLeftOpen, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/shared/ui/core";
import { cn } from "@/shared/lib/cn";
import { GlassPanel } from "../GlassPanel/GlassPanel";
import type { NavMap } from "../navigation/navigation";
import type { ShellUser } from "../AppShell/useShellUser";
import { SidebarBrand } from "./SidebarBrand";
import { SidebarNavGroup } from "./SidebarNavGroup";
import { SidebarUser } from "./SidebarUser";

export interface AppSidebarProps {
  nav: NavMap;
  user: ShellUser | null;
  collapsed?: boolean;
  /** Si se pasa, aparece el botón de plegar a riel. */
  onToggleCollapsed?: () => void;
  /** Si se pasa, aparece la X de cerrar (el cajón móvil). */
  onClose?: () => void;
  /** Se llama al pulsar cualquier enlace. */
  onNavigate?: () => void;
  className?: string;
}

/**
 * La barra lateral: un panel de vidrio FLOTANTE, no una columna pegada al
 * borde. La separación del borde es lo que da la sensación de capas de la
 * referencia; el color y el material son los del login.
 *
 * ── Por qué no recorta el contenido ──────────────────────────────────
 * Nada de `overflow-hidden` en el panel: las etiquetas flotantes del riel
 * plegado salen POR FUERA, y un recorte las mataría justo en el estado en
 * el que hacen falta. Cuando está desplegada la navegación sí puede
 * desplazarse (`overflow-y-auto`); plegada no, porque un contenedor con
 * desplazamiento vertical también recorta en horizontal.
 *
 * ── Un solo componente para escritorio y para el cajón ───────────────
 * El cajón móvil monta ESTA misma barra con `onClose`. Duplicarla para
 * móvil es lo que hace que un enlace nuevo aparezca en una y no en la
 * otra.
 */
export function AppSidebar({
  nav,
  user,
  collapsed = false,
  onToggleCollapsed,
  onClose,
  onNavigate,
  className,
}: AppSidebarProps) {
  const { t } = useTranslation();

  return (
    <GlassPanel
      /* Ya no hay brillo que elegir: `GlassPanel` retiró la prop `sheen`
         al aplanarse (26-ago). Este panel era el que peor lo llevaba —alto
         y estrecho, la diagonal lo cruzaba entero y se leía como una banda
         gris—, así que aquí el cambio solo quita un problema. */
      as="aside"
      className={cn("flex h-full flex-col", className)}
      aria-label={t("Barra lateral")}
    >
      <div
        className={cn(
          "relative flex items-center gap-2 px-3 pb-3 pt-4",
          collapsed && "flex-col gap-2 px-2",
        )}
      >
        <SidebarBrand collapsed={collapsed} className={cn(!collapsed && "flex-1")} />

        {onClose && (
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            onClick={onClose}
            aria-label={t("Cerrar navegación")}
            leftIcon={<X />}
            className="shrink-0 bg-white/60"
          />
        )}

        {onToggleCollapsed && (
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            onClick={onToggleCollapsed}
            aria-label={collapsed ? t("Desplegar la barra lateral") : t("Plegar la barra lateral")}
            aria-pressed={collapsed}
            leftIcon={collapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
            className="shrink-0 bg-white/60"
          />
        )}
      </div>

      <nav
        aria-label={t("Navegación principal")}
        className={cn(
          "relative min-h-0 flex-1 px-3 pb-3",
          collapsed ? "overflow-visible px-2" : "overflow-y-auto",
        )}
      >
        {nav.map((section) => (
          <SidebarNavGroup
            key={section.label}
            section={section}
            collapsed={collapsed}
            onNavigate={onNavigate}
          />
        ))}
      </nav>

      <div className="relative border-t border-ds-border pt-3">
        <SidebarUser user={user} collapsed={collapsed} />
      </div>
    </GlassPanel>
  );
}
